import { AxiosResponse } from 'axios';
import { colors } from '../common/pallette/colors';

export type ThenArgs<T> = T extends Promise<infer U> ? U : T;

export type AxiosArgs<T> = T extends AxiosResponse<infer U> ? U : undefined;

export type ArrayElement<A> = A extends readonly (infer T)[] ? T : never;

export type Identifier = string | number;

export type ErrorObject = {
  status: number;
  StackTrace: string | null;
  UserMessage: string | null;
};

export type League = {
  id: Identifier;
  name: string;
};

export type Club = {
  id: Identifier;
  name: string;
  currentRating: StarRating;
  targetRating: StarRating;
  imageUrl: string;
  color: string;
  textColor: string;
  leagueId: Identifier;
  selfAssessment: number;
  evaluation: number;
  evaluationRAGB: RAGB;
  perfPlan: number;
  perfPlanRAGB: RAGB;
  perfPlanStatus: ActionStatus;
  strategy: number;
  strategyRAGB: RAGB;
  licence: number;
  licenceRAGB: RAGB;
  licenceStatus: ActionStatus;
  licenceEvaluation: number;
};

export type ClubUser = {
  id: Identifier;
  firstName: string;
  lastName: string;
  userName: string;
  roleId: Identifier;
  departmentId: Identifier;
};

export type ClubProject = {
  id: Identifier;
  displayName: string;
};

export enum TimeLineEventType {
  Target = 0,
  Project = 1,
  SelfAssessment = 2,
  OnSiteStart = 3,
  OnSiteEnd = 4,
  SelfAssessment2 = 5,
  PostReportReview = 6,
  NextTarget = 7,
}

export const TimeLineEventColor = {
  [TimeLineEventType.Project]: colors.azure,
  [TimeLineEventType.SelfAssessment]: colors.rosy,
  [TimeLineEventType.OnSiteStart]: colors.tangerine,
  [TimeLineEventType.OnSiteEnd]: colors.veryLightPink,
  [TimeLineEventType.SelfAssessment2]: colors.iris,
  [TimeLineEventType.PostReportReview]: colors.shamrockGreen,
  [TimeLineEventType.Target]: colors.azure,
  [TimeLineEventType.NextTarget]: colors.lightishRed,
};

export const TimeLineEventName = {
  [TimeLineEventType.Project]: 'PROJECT',
  [TimeLineEventType.SelfAssessment]: 'SELF_ASSESSMENT',
  [TimeLineEventType.OnSiteStart]: 'ON_SITE_START',
  [TimeLineEventType.OnSiteEnd]: 'ON_SITE_END',
  [TimeLineEventType.SelfAssessment2]: 'SELF_ASSESSMENT_2',
  [TimeLineEventType.PostReportReview]: 'POST_REPORT_REVIEW',
  [TimeLineEventType.Target]: 'TARGET',
  [TimeLineEventType.NextTarget]: 'NEXT_TARGET',
};

export type TimeLineEvent = {
  type: TimeLineEventType;
  date: string;
  title: string | null;
};

export type ClubEvaluation = {
  targetStarRating: StarRating;
  currentStarRating: StarRating;
  targetStatus: ActionStatus | null;
  selfAssessmentProgress: number;
  evaluationProgress: number;
  timelineEvents: TimeLineEvent[];
  daysToTimelineEvents: {
    finalReport: number;
    interimReport: number;
    onSiteEnd: number;
    onSiteStart: number;
    postReportReview: number;
    selfAssessment: number;
    selfAssessment2: number;
    target: number;
  };
};

export type User = {
  id: Identifier;
  userName: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  languageCode: string;
  departmentId: Identifier;
};

export type Chat = {
  chatId: Identifier;
  hasUnreadMessages: boolean;
  user: User & { clubName: string };
};

export type ChatMessage = {
  id: Identifier;
  message: string;
  isRead: boolean;
  userId: Identifier;
  createdDate: string;
};

export type Project = {
  id: Identifier;
  name: string;
  dueDate: string;
  ownerName: string;
  departmentName: string;
  ragb: RAGB;
  progress: number;
  arrowDirection: ArrowDirection;
};

export type ProjectDetails = {
  name: string;
  description: string;
  status: ActionStatus;
  progress: number;
  startDate: Date;
  dueDate: Date;
  clubId: Identifier;
  assigneeUserId?: Identifier;
  departmentId?: Identifier;
  statusComment: string;
  actions: Action[];
  pastStatus?: ActionStatus;
  pastStatusDate?: string;
  isArchived: boolean;
};

export type Criteria = {
  id: Identifier;
  uid: string;
  name: string;
  ragb: RAGB;
  progress: number;
  status: SiteReviewStatus;
  criteriaGroupName: string;
  isReady?: boolean;
  restrictions: { isRestricted: boolean; userId: number }[];
};

export type CriteriaShort = {
  id: Identifier;
  uid: string;
  name: string;
  description: string;
  criteriaGroupName: string;
};

export type EvaluationCriteria = {
  id: Identifier;
  uid: string;
  name: string;
  ragb: RAGB | null;
  progress: number;
  status: SiteReviewStatus;
  arrowDirection: ArrowDirection;
  criteriaGroupName: string;
};

export enum SelfAssessmentStatus {
  NotStarted,
  InProgress,
  Ready,
  Complete,
}

export enum Roles {
  JLeagueAdmin = 'JLeagueAdmin',
  ClubChecker = 'ClubChecker',
  ClubMaker = 'ClubMaker',
}

export const roles = {
  JLeagueAdmin: 'JLeagueAdmin',
  ClubChecker: 'ClubChecker',
  ClubMaker: 'ClubMaker',
}

export const selfAssessmentStatusDisplayName:any = {
  [SelfAssessmentStatus.NotStarted]: 'NOT_STARTED',
  [SelfAssessmentStatus.InProgress]: 'IN_PROGRESS',
  [SelfAssessmentStatus.Ready]: 'READY',
  [SelfAssessmentStatus.Complete]: 'COMPLETE',
};

export enum FutureProgressStatus {
  Outstanding = 0,
  Complete = 1,
}
export type FutureProgressCriteria = {
  id: Identifier;
  uid: string;
  name: string;
  description: string;
  evaluationRating: SiteReviewStatus;
  ragb: RAGB;
  progressStatus: FutureProgressStatus;
  isExceeded: boolean;
};

export const futureProgressStatusDisplayName = {
  [FutureProgressStatus.Complete]: 'COMPLETE',
  [FutureProgressStatus.Outstanding]: 'OUTSTANDING',
};

export enum FinalReportStatus {
  Missed,
  Outstanding,
  Complete,
}
export enum InterimReportStatus {
  Missed,
  Outstanding,
  Complete,
}

export const interimReportStatusDisplayName = {
  [InterimReportStatus.Missed]: 'MISSED',
  [InterimReportStatus.Outstanding]: 'OUTSTANDING',
  [InterimReportStatus.Complete]: 'COMPLETE',
};

export const finalReportStatusDisplayName = {
  [InterimReportStatus.Missed]: 'MISSED',
  [InterimReportStatus.Outstanding]: 'OUTSTANDING',
  [InterimReportStatus.Complete]: 'COMPLETE',
};

export enum StarRating {
  NoRating = 0,
  A = 1,
  B = 2,
  C = 3,
  D = 4,
}

export enum AssessmentGroupStatus {
  Missed = 0,
  Outstanding = 1,
  Complete = 2,
}

export const assessmentGroupDisplayName = {
  [AssessmentGroupStatus.Missed]: 'MISSED',
  [AssessmentGroupStatus.Outstanding]: 'OUTSTANDING',
  [AssessmentGroupStatus.Complete]: 'COMPLETE',
};

export type DesirePlan = {
  id: Identifier;
  year: number;
  starRating: StarRating;
  achievedStarRating: StarRating;
  previousTargetStarRating: StarRating;
  clubCurrentRating: StarRating;
  targetDate: string;
  isReadyToReview: boolean;
  targetText: string;
  targetRAGB: RAGB;
  interimReportIssued: boolean;
  finalReportIssued: boolean;
  dates?: {
    onSiteStartDate: string;
    onSiteEndDate: string;
    postReportReviewDate: string;
    selfAssessment1Date: string;
    selfAssessment2Date: string;
  };
  starRatingDescriptions: [
    {
      starRating: StarRating.A;
      name: string;
      description: string;
    },
    {
      starRating: StarRating.B;
      name: string;
      description: string;
    },
    {
      starRating: StarRating.C;
      name: string;
      description: string;
    },
    {
      starRating: StarRating.D;
      name: string;
      description: string;
    },
  ];
};

export type PerformancePlan = {
  evaluationActionsPieChart: {
    statuses: ActionStatuses;
    pastStatuses: PastStatuses;
  };
  projectsPieChart: {
    statuses: ActionStatuses;
    pastStatuses: PastStatuses;
  };
  strategyActionsPieChart: {
    statuses: ActionStatuses;
    pastStatuses: PastStatuses;
  };
  quickView: {
    totalActions: number;
    jlRecommended: number;
    unassigned: number;
    missed: number;
  };
  bubblesTimeline: StatusesInPeriod;
};

export type Strategy = {
  goals: {
    statuses: ActionStatuses;
    pastStatuses: PastStatuses;
  };
  quickView: {
    total: number;
    jlDefined: number;
    unassigned: number;
    missed: number;
  };
  isKpiUpdated: boolean;
  timelines: StatusesInPeriod;
};

export type EvaluationReport = {
  id: Identifier;
  year: number;
  targetStarRating: number;
  achievedStarRating: number;
  actionPlanTimeline: number;
  isOverrideRating: boolean;
  type: ReportType;
  status: ReportStatus;
  date: string;
};

export type DefaultAction = {
  id: Identifier;
  name: string;
  description: string;
};

export type SupportQuestion = {
  id: Identifier;
  name: string;
  jaName: string;
};

export type GuidanceItem = {
  id: Identifier;
  name: string;
  image: string;
  boxName?: string;
  boxImage?: string;
  isBox: boolean;
  questions: Array<{
    id: Identifier;
    question: string;
    answer: string;
    image: string;
  }>;
};

export type Standard = {
  id: Identifier;
  uid: string;
  name: string;
  description: string;
  starRating: number;
  status: SelfAssessmentStatus;
  progress?: number;
  ragb?: RAGB;
  criteriaId: Identifier;
  restrictions: { isRestricted: boolean; userId: number }[];
};

export type ActionStandardMetadata = {
  id: Identifier;
  uid: string;
  name: string;
  starRating: number;
  description: string;
  year: number;
};

export type StandardFile = {
  id: Identifier;
  name: string;
  type: string;
  downloadUrl: string;
};

export type Action = {
  id: Identifier;
  name: string;
  description: string;
  status: SiteReviewStatus;
  ragb: RAGB;
  dueDate: string;
  startDate: string;
  standardId: Identifier;
  clubId: Identifier;
  defaultActionId: Identifier;
  projectId: Identifier;
  departmentId: Identifier;
  ownerUserId: Identifier;
  projectName: string;
  departmentName: string;
  ownerName: string;
  clubDesirePlanId: Identifier;
  isCreatedByJL: boolean;
  licenceJLActionStatus: boolean;
  isAlreadyComplete: boolean;
  statusComment: string;
  arrowDirection: ArrowDirection;
  pastStatus?: ActionStatus;
  pastStatusDate?: string;
  isArchived: boolean;
  tasks: {
    id: number;
    task: string;
    checked: boolean;
  }[];
  objectives: {
    id: Identifier;
  }[];
  standards: {
    standardId: Identifier;
    name: string;
    criteriaId: Identifier;
    criteriaName: string;
    criteriaDescription: string;
    starRating: number;
    year: number;
  }[];
  licenseStandards: {
    standardId: Identifier;
    name: string;
    criteriaId: Identifier;
    criteriaName: string;
    criteriaDescription: string;
    year: number;
  }[];
};

export type ActionCreateForm = {
  name: string;
  description: string;
  status: ActionStatus;
  dueDate: string;
  startDate: string;
  standardId: Identifier;
  clubId: Identifier;
  defaultActionId: Identifier;
  projectId: Identifier;
  ownerUserId: Identifier;
  departmentId: Identifier;
  statusComment: string;
  tasks: {
    id: number;
    task: string;
    checked: boolean;
  }[];
  objectives: {
    id: Identifier;
    draft: DraftField;
  }[];
  standards: {
    standardId: Identifier;
    criteriaId: Identifier;
    starRating: number;
    draft: DraftField;
  }[];
  licenceStandards: {
    licenceStandardId: Identifier;
    criteriaId: Identifier;
    starRating: number;
    draft: DraftField;
  }[];
};

export type ActionInfo = {
  id: Identifier;
  uid: string;
  name: string;
  isCreatedByJL: boolean;
  description: string;
  dueDate: string;
  ownerName?: string;
  departmentName?: string;
  projectName?: string;
  defaultActionId?: Identifier;
  arrowDirection: ArrowDirection;
  ragb: RAGB;
};

export enum CalendarEventType {
  PROJECT = 1,
  ACTION = 2,
  GOAL = 3,
  OBJECTIVE = 4,
}

export type CalendarEvent = {
  id: Identifier;
  type: CalendarEventType;
  name: string;
  description: string;
  dueDate: string;
  isReadByOwner: boolean;
};

export type FileMeta = {
  id: Identifier;
  uid: string;
  name: string;
  type: string;
  clubComment: string;
};

export type EvidenceLibraryItem = {
  id: Identifier;
  title: string;
  uid: string;
  uploaded: string;
  languageCode: 'EN' | 'JA';
  translated: boolean;
  attachedAfterInterimReport: boolean;
  converted: boolean;
  inQueue: boolean;
  evaluationCriteria: {
    id: Identifier;
    name: string;
    standards: {
      id: Identifier;
      name: string;
    }[];
  }[];
  licenceCriteria: {
    id: Identifier;
    name: string;
    standards: {
      id: Identifier;
      name: string;
    }[];
  }[];
};

export type Department = {
  id: Identifier;
  name: string;
};

export type TimeSpent = {
  ticks: number;
  days: number;
  hours: number;
  milliseconds: number;
  minutes: number;
  seconds: number;
  totalDays: number;
  totalHours: number;
  totalMilliseconds: number;
  totalMinutes: number;
  totalSeconds: number;
};

export type OnSiteSession = {
  id: Identifier;
  name: string;
  roles: string[];
  timeSpent: TimeSpent;
  status: SiteReviewStatus;
  type: SessionType;
};

export type OffSiteEvaluation = {
  status: SiteReviewStatus;
  comment: string;
};

export type OnSiteCriteria = {
  id: Identifier;
  uid: string;
  name: string;
  offSiteStatus: SiteReviewStatus;
  timeSpent: TimeSpent;
  onSiteStatus: SiteReviewStatus;
};

export type InterviewUser = {
  id: Identifier;
  userId: Identifier;
  displayName: string;
};

export type SessionDate = string | null;

export type AgeGroup = {
  id: Identifier;
  name: string;
};

export type ObservationAgeGroup = {
  id: Identifier;
  ageGroupId: Identifier;
  description: string;
};

export type OnSiteQuestionObject = {
  id: Identifier;
  standardId: Identifier;
  standardName: string;
  standardStarRating: number;
  questionId: Identifier;
  question: string;
  comment: string;
  status: number;
  offSiteStatus: number;
  offSiteComment: number;
  isFutureProgress: boolean;
};

export enum SessionType {
  INTERVIEW = 1,
  OBSERVATION = 2,
}

export type Interviewee = {
  id: Identifier | null;
  userId?: Identifier;
  name: string;
  roleId: number | null;
  roleName: string | null;
  departmentId: number | null;
  role: string;
};

export type OffSiteCriteria = {
  id: Identifier;
  uid: string;
  name: string;
  progress: number;
  status: SiteReviewStatus;
};
export enum FileDeleteStatus {
  Detached = 0,
  Deleted = 1,
}

export type OffSiteStandard = {
  id: Identifier;
  uid: string;
  name: string;
  starRating: number;
  status: SiteReviewStatus;
};

export type SessionAnswer = {
  question: string;
  comment: string;
  status: SiteReviewStatus;
};

export type OnSiteEvaluation = {
  sessionId: Identifier;
  sessionName: string;
  sessionType: SessionType;
  sessionDate: string;
  ageGroups: AgeGroup[];
  sessionAnswers: SessionAnswer[];
  roles: {
    id: Identifier;
    name: string;
  }[];
  interviewees: Interviewee[];
};

export enum ActionGroupType {
  Total,
  JLRecommended,
  Unassigned,
  Missed,
  Evaluation,
}

export const actionGroupOptions = [
  { label: 'TOTAL', value: ActionGroupType.Total },
  { label: 'JL_RECOMMENDED', value: ActionGroupType.JLRecommended },
  { label: 'UNASSIGNED', value: ActionGroupType.Unassigned },
  { label: 'MISSED', value: ActionGroupType.Missed },
  { label: 'EVALUATION', value: ActionGroupType.Evaluation },
];
export const jlActionGroupOptions = [
  { label: 'TOTAL', value: ActionGroupType.Total },
  { label: 'UNASSIGNED', value: ActionGroupType.Unassigned },
  { label: 'MISSED', value: ActionGroupType.Missed },
];

export enum StrategyGroupType {
  Total,
  JLDefined,
  Unassigned,
  Missed,
}

export const strategyGroupOptions = [
  { label: 'TOTAL', value: StrategyGroupType.Total },
  { label: 'JL_DEFINED', value: StrategyGroupType.JLDefined },
  { label: 'UNASSIGNED', value: StrategyGroupType.Unassigned },
  { label: 'MISSED', value: StrategyGroupType.Missed },
];

export enum SiteReviewStatus {
  NotEvaluated = 0,
  NotMet = 1,
  Partial = 2,
  Met = 3,
}

export enum ActionStatus {
  Missed = 0,
  AtRisk = 1,
  OnTrack = 2,
  Complete = 3,
  NotStarted = 4,
}

export enum LicenceStatus {
  NotEvaluated = 0,
  NotMet = 1,
  Partial = 2,
  Met = 3,
}

export const actionStatusDisplayNames = {
  [ActionStatus.Missed]: 'MISSED',
  [ActionStatus.AtRisk]: 'AT_RISK',
  [ActionStatus.OnTrack]: 'ON_TRACK',
  [ActionStatus.Complete]: 'COMPLETE',
  [ActionStatus.NotStarted]: 'NOT_STARTED',
};

export const siteReviewStatusOptions = [
  { label: 'NOT_MET', value: SiteReviewStatus.NotMet },
  { label: 'PARTIAL', value: SiteReviewStatus.Partial },
  { label: 'MET', value: SiteReviewStatus.Met },
];

export const siteReviewStatusDisplayName = {
  [SiteReviewStatus.Met]: 'MET',
  [SiteReviewStatus.NotMet]: 'NOT_MET',
  [SiteReviewStatus.NotEvaluated]: 'NOT_EVALUATED',
  [SiteReviewStatus.Partial]: 'PARTIAL',
};

export const siteReviewStatusColors = {
  [SiteReviewStatus.Met]: colors.shamrockGreen,
  [SiteReviewStatus.NotMet]: colors.lightishRed,
  [SiteReviewStatus.NotEvaluated]: colors.grey,
  [SiteReviewStatus.Partial]: colors.tangerine,
};

export const actionStatusOptions = [
  { label: 'NOT_STARTED', value: ActionStatus.NotStarted },
  { label: 'MISSED', value: ActionStatus.Missed },
  { label: 'AT_RISK', value: ActionStatus.AtRisk },
  { label: 'ON_TRACK', value: ActionStatus.OnTrack },
  { label: 'COMPLETE', value: ActionStatus.Complete },
];

export const actionStatusColors = {
  [ActionStatus.Missed]: colors.lightishRed,
  [ActionStatus.AtRisk]: colors.tangerine,
  [ActionStatus.OnTrack]: colors.shamrockGreen,
  [ActionStatus.Complete]: colors.gold,
  [ActionStatus.NotStarted]: colors.darkLightGrey,
};

export const licenceStatusOptions = [
  { label: 'NOT_MET', value: LicenceStatus.NotMet },
  { label: 'MET', value: LicenceStatus.Met },
];

export const licenceStatusDisplayName = {
  [LicenceStatus.Met]: 'MET',
  [LicenceStatus.NotMet]: 'NOT_MET',
  [LicenceStatus.NotEvaluated]: 'NOT_EVALUATED',
  [LicenceStatus.Partial]: 'PARTIAL',
};

export const licenceStatusColors = {
  [LicenceStatus.Met]: colors.shamrockGreen,
  [LicenceStatus.NotMet]: colors.lightishRed,
  [LicenceStatus.NotEvaluated]: colors.grey,
  [LicenceStatus.Partial]: colors.tangerine,
};

export enum SearchType {
  Criteria,
  Standard,
  Project,
  Action,
  Evidence,
  Goal = 5,
  Objective = 6,
  Kpi = 7,
}

export enum ReportType {
  Interim = 1,
  Final = 2,
}

export enum ReportStatus {
  Draft = 1,
  Issued = 2,
}

export const reportTypeName = {
  [ReportType.Interim]: 'INTERIM_REPORT',
  [ReportType.Final]: 'FINAL_REPORT',
};

export const reportStatusName = {
  [ReportStatus.Draft]: 'DRAFT',
  [ReportStatus.Issued]: 'ISSUED',
};

export type ReportData = {
  id: Identifier;
  clubName: string;
  imageBase64: string;
  reportStatus: ReportStatus;
  reportDate: string;
  executiveSummary: {
    year: number;
    targetStarRating: StarRating;
    achievedStarRating: StarRating;
    type: ReportType;
  };
  clubOverview: string;
  keyAchievements: string;
  developmentAreas: string;
  overview: {
    [SiteReviewStatus.Met]: number;
    [SiteReviewStatus.NotMet]: number;
    [SiteReviewStatus.Partial]: number;
    [SiteReviewStatus.NotEvaluated]: number;
  };
  criteria: Array<{
    id: Identifier;
    name: string;
    status: SiteReviewStatus;
    overview: {
      [SiteReviewStatus.Met]: number;
      [SiteReviewStatus.NotMet]: number;
      [SiteReviewStatus.Partial]: number;
      [SiteReviewStatus.NotEvaluated]: number;
    };
    standards: Array<{
      id: Identifier;
      name: string;
      description: string;
      status: ActionStatus;
      starRating: StarRating;
      jlComment: string;
      actions: Array<{
        id: Identifier;
        name: string;
        description: string;
      }>;
    }>;
  }>;
  sessions: Array<{
    id: Identifier;
    name: string;
    type: SessionType;
    date: string;
    conducted: Array<{
      id: Identifier;
      displayName: string;
      role: string;
      email: string;
      imageBase64: string;
    }>;
    observed: string[];
    required: string[];
  }>;
  evaluationTeam: Array<{
    id: Identifier;
    displayName: string;
    role: string;
    email: string;
    imageBase64: string;
  }>;
  glossaryOfTerms: Array<{
    id: Identifier;
    name: string;
    description: string;
  }>;
};

export type AnalyticValues = {
  [key in number | ValueKey]: RAGB;
};

export type AnalyticData = {
  id: Identifier;
  name: string;
  values: AnalyticValues;
  standards: {
    id: Identifier;
    name: string;
    values: AnalyticValues;
  };
};

export type AnalyticCardInfo = {
  clubId: number | null;
  clubName: string | null;
  criteriaName: string | null;
  type: HeaderType;
  statusType: AnalyticStatusType;
  statuses: { [key: string]: number };
};

export type Analytics = {
  headers: AnalyticHeader[];
  data: AnalyticData[];
};

export enum AnalyticStatusType {
  ActionStatus = 0,
  SiteReviewStatus = 1,
}

export enum RAGB {
  RED,
  AMBER,
  GREEN,
  BLUE,
  GREY = 4,
}

export const matchActionStatusNameToRAGB:any = {
  [RAGB.RED]: actionStatusDisplayNames[ActionStatus.Missed],
  [RAGB.AMBER]: actionStatusDisplayNames[ActionStatus.AtRisk],
  [RAGB.GREEN]: actionStatusDisplayNames[ActionStatus.OnTrack],
  [RAGB.BLUE]: actionStatusDisplayNames[ActionStatus.Complete],
  [RAGB.GREY]: actionStatusDisplayNames[ActionStatus.NotStarted],
};

export enum HeaderType {
  Club = 0,
  Average = 1,
  EvaluationScoresCurrent = 2,
  EvaluationScoresLastReport = 3,
  EvaluationScoresLastReportPlusOne = 4,
  EvaluationScoresTargetStarAverage = 5,
  EvaluationScoresLeagueAverage = 6,
  EvaluationScoresAllClubsAverage = 7,
  EvaluationActionsCurrent = 8,
  EvaluationActionsLastWeek = 9,
  EvaluationActionsLastWeekPlusOne = 10,
  EvaluationActionsTargetStarAverage = 11,
  EvaluationActionsLeagueAverage = 12,
  EvaluationActionsAllClubsAverage = 13,
  EvaluationScoresCriteria = 14,
  EvaluationActionsCriteria = 15,
}

export enum ValueKey {
  Average = 'average',
  EvaluationScoresCurrent = 'evaluationScoresCurrent',
  EvaluationScoresLastReport = 'evaluationScoresLastReport',
  EvaluationScoresLastReportPlusOne = 'evaluationScoresLastReportPlusOne',
  EvaluationScoresTargetStarAverage = 'evaluationScoresTargetStarAverage',
  EvaluationScoresLeagueAverage = 'evaluationScoresLeagueAverage',
  EvaluationScoresAllClubsAverage = 'evaluationScoresAllClubsAverage',
  EvaluationActionsCurrent = 'evaluationActionsCurrent',
  EvaluationActionsLastWeek = 'evaluationActionsLastWeek',
  EvaluationActionsLastWeekPlusOne = 'evaluationActionsLastWeekPlusOne',
  EvaluationActionsTargetStarAverage = 'evaluationActionsTargetStarAverage',
  EvaluationActionsLeagueAverage = 'evaluationActionsLeagueAverage',
  EvaluationActionsAllClubsAverage = 'evaluationActionsAllClubsAverage',
}
export const headerTypeNames = {
  [HeaderType.Average]: 'AVERAGE',
  [HeaderType.EvaluationScoresCurrent]: 'CURRENT',
  [HeaderType.EvaluationScoresLastReport]: 'LAST_REPORT',
  [HeaderType.EvaluationScoresLastReportPlusOne]: 'LAST_REPORT_PLUS_ONE',
  [HeaderType.EvaluationScoresTargetStarAverage]: 'TARGET_STAR_AVERAGE',
  [HeaderType.EvaluationScoresLeagueAverage]: 'LEAGUE_AVERAGE',
  [HeaderType.EvaluationScoresAllClubsAverage]: 'ALL_CLUBS_AVERAGE',
  [HeaderType.EvaluationActionsCurrent]: 'CURRENT',
  [HeaderType.EvaluationActionsLastWeek]: 'LAST_WEEK',
  [HeaderType.EvaluationActionsLastWeekPlusOne]: 'LAST_WEEK_PLUS_ONE',
  [HeaderType.EvaluationActionsTargetStarAverage]: 'TARGET_STAR_AVERAGE',
  [HeaderType.EvaluationActionsLeagueAverage]: 'LEAGUE_AVERAGE',
  [HeaderType.EvaluationActionsAllClubsAverage]: 'ALL_CLUBS_AVERAGE',
  [HeaderType.EvaluationActionsCriteria]: 'CRITERIA',
  [HeaderType.EvaluationScoresCriteria]: 'CRITERIA',
};

export type AnalyticHeader = {
  valueKey: ValueKey;
  clubName: string | null;
  type: HeaderType;
  clubImageUrl?: string | null;
};

export enum AnalyticClubFilterType {
  League,
  TargetRating,
  CurrentRating,
  Cycle,
}

export enum AnalyticDataType {
  EvaluationScores,
  EvaluationActions,
  StrategicGoals,
}

export const analyticDataTypeOptions = [
  { label: 'EVALUATION_SCORES', value: AnalyticDataType.EvaluationScores },
  { label: 'EVALUATION_ACTIONS', value: AnalyticDataType.EvaluationActions },
  // { label: 'STRATEGIC_GOALS', value: AnalyticDataType.StrategicGoals },
];

export const RAGB_COLORS:any = {
  [RAGB.RED]: colors.lightishRed,
  [RAGB.AMBER]: colors.tangerine,
  [RAGB.GREEN]: colors.vibrantGreen,
  [RAGB.BLUE]: colors.gold,
  [RAGB.GREY]: colors.middleGrey,
};

export type CriteriaGroup = {
  id: Identifier;
  name: string;
  description: string;
};

// Strategy

export type ObjectiveShort = {
  id: Identifier;
  name: string;
  dueDate: string;
  ownerId: Identifier;
  ownerName: string;
  departmentId: Identifier;
  departmentName: string;
  goalId: Identifier;
  status: ActionStatus;
  isJLRecommended: boolean;
  currentPoint: number;
  targetPoint: number;
  arrowDirection: ArrowDirection;
};

export type GoalShort = {
  id: Identifier;
  name: string;
  status: ActionStatus;
  dueDate: string;
  ownerId: Identifier;
  ownerName: string;
  departmentId: Identifier;
  departmentName: string;
  progress: number;
  isJLRecommended: boolean;
  arrowDirection: ArrowDirection;
};

export type Goal = {
  id: Identifier;
  name: string;
  description: string;
  ownerId: Identifier;
  departmentId: Identifier;
  dueDate: string;
  status: ActionStatus;
  progress: number;
  isJLRecommended: boolean;
  objectives: ObjectiveShort[];
  pastStatus?: ActionStatus;
  pastStatusDate?: string;
};

export enum Direction {
  Greater = 1,
  EqualOrGreater = 2,
  Equal = 3,
  EqualOrLess = 4,
  Less = 5,
}

export enum DatePeriodType {
  ToThreeMonths,
  FromThreeToSixMonths,
  FromSixToTwelveMonths,
  FromOneToTwoYears,
  TwoYearsPlus,
}

export const DatePeriodTypeNames = {
  [DatePeriodType.ToThreeMonths]: 'TO_3_MONTHS',
  [DatePeriodType.FromThreeToSixMonths]: 'FROM_3_TO_6_MONTHS',
  [DatePeriodType.FromSixToTwelveMonths]: 'FROM_6_TO_12_MONTHS',
  [DatePeriodType.FromOneToTwoYears]: 'FROM_1_TO_2_YEARS',
  [DatePeriodType.TwoYearsPlus]: '2_YEARS_PLUS',
};

export enum TimelineSelectorType {
  Empty,
  SixWeeks = 42,
  ThreeMonths = 91,
  SixMonths = 182,
  OneYear = 365,
}

export const TimelineSelectorTypeNames = {
  [TimelineSelectorType.Empty]: '',
  [TimelineSelectorType.SixWeeks]: 'SIX_WEEKS',
  [TimelineSelectorType.ThreeMonths]: 'THREE_MONTHS',
  [TimelineSelectorType.SixMonths]: 'SIX_MONTHS',
  [TimelineSelectorType.OneYear]: 'ONE_YEAR',
};

export const TimelineSelectorOptions = [
  { label: '...', value: TimelineSelectorType.Empty },
  { label: 'SIX_WEEKS', value: TimelineSelectorType.SixWeeks },
  { label: 'THREE_MONTHS', value: TimelineSelectorType.ThreeMonths },
  { label: 'SIX_MONTHS', value: TimelineSelectorType.SixMonths },
  { label: 'ONE_YEAR', value: TimelineSelectorType.OneYear },
];

export type StatusesInPeriod = {
  [key in keyof typeof DatePeriodType]: { [key in keyof typeof ActionStatus]: number };
};

export type Objective = {
  id: Identifier;
  name: string;
  direction: Direction;
  targetPoint: number;
  dueDate: string;
  ownerId: Identifier;
  departmentId: Identifier;
  goalId: Identifier;
  status: ActionStatus;
  isJLRecommended: boolean;
  actions: Action[];
  kpiId?: Identifier;
  pastStatus?: ActionStatus;
  pastStatusDate?: string;
};

export enum KPIDataType {
  Number = 1,
  Currency = 2,
  Position = 3,
  Percentage = 4,
}

export enum KPIMode {
  KpiDetails = 0,
  ObjectiveDetails = 1,
}

export const KPIDataTypeNames = {
  [KPIDataType.Number]: 'NUMBER',
  [KPIDataType.Currency]: 'CURRENCY',
  [KPIDataType.Position]: 'POSITION',
  [KPIDataType.Percentage]: 'PERCENTAGE',
};

export enum KPIFrequency {
  Weekly = 1,
  Forthnightly = 2,
  Monthly = 3,
  Quarterly = 4,
  SemiAnnually = 5,
  Annually = 6,
}

export const KPIFrequencyNames = {
  [KPIFrequency.Weekly]: 'WEEKLY',
  [KPIFrequency.Forthnightly]: 'FORTHNIGHTLY',
  [KPIFrequency.Monthly]: 'MONTHLY',
  [KPIFrequency.Quarterly]: 'QUARTERLY',
  [KPIFrequency.SemiAnnually]: 'SEMIANNUALLY',
  [KPIFrequency.Annually]: 'ANNUALLY',
};

export type KPIShort = {
  id: Identifier;
  name: string;
  description: string;
  dataType: KPIDataType;
  frequency: KPIFrequency;
  objectivesCount: number;
  dueDate: string;
  value: number;
  isUpdated: boolean;
  isJLRecommended: boolean;
};

export enum KPIChartPointType {
  Objective = 1,
  KpiValue = 2,
}

export type KPIChartPoint = {
  timestamp: number;
  value: number;
  objectiveValue: number;
  objectiveStatus: RAGB;
  type: KPIChartPointType;
};

export type KPIValue = {
  id: Identifier;
  date: string;
  value: number;
  kpiId: Identifier;
};

export type KPIDetails = {
  id: Identifier;
  name: string;
  description: string;
  dataType: KPIDataType;
  frequency: KPIFrequency;
  isUpdated: boolean;
  kpiValues: KPIValue[];
  kpiChartPoints: KPIChartPoint[];
  objectives: ObjectiveShort[];
};

export enum PastStatusPeriod {
  LastWeek,
  LastMonth,
}
export enum ArchivedStatus {
  All,
  Active,
  Archived,
}

export enum DepartmentsReportType {
  Projects,
  EvaluationActions,
  AllActions,
}

export enum ArrowDirection {
  Down,
  Right,
  Up,
}

export type ActionStatuses = {
  [key in keyof typeof ActionStatus]: number;
};

export type SiteReviewStatuses = {
  [key in keyof typeof SiteReviewStatus]: number;
};

export type PastStatuses = {
  [key in keyof typeof ActionStatus]: {
    [key in keyof typeof ArrowDirection]: number;
  };
};

export const TablesSortPrefix = 'sort_order_';

export enum LanguageCode {
  Japanese = 'ja',
  English = 'en',
}

export enum TimelineItemsType {
  Projects,
  EvaluationActions,
  AllActions,
  Objectives,
}

export const TimelineItemsTypeDisplayName = {
  [TimelineItemsType.Projects]: 'PROJECT',
  [TimelineItemsType.EvaluationActions]: 'EVALUATION_ACTION',
  [TimelineItemsType.AllActions]: 'ACTION',
  [TimelineItemsType.Objectives]: 'OBJECTIVE',
};

type DraftField = boolean | undefined;

export enum ReportingPeriod {
  Weekly,
  Monthly,
}

export type EvaluationReportAction = {
  id: number;
  name: string;
  description: string;
  isAlreadyComplete: boolean;
  reportType: ReportType;
};

export type EvaluationReportStandardDetails = {
  status: SiteReviewStatus;
  disableIsAlreadyCompleteButton: boolean;
  interimReportIssued: boolean;
  comment: string;
  attachedAfterInterimReport: boolean; // label for marking as new
  actions: EvaluationReportAction[];
};

export type ExecutiveSummary = {
  clubOverview: string | null;
  keyAchievements: string | null;
  keyAreasToImprove: string | null;
  isOverrideRating: boolean;
  isActionPlanIncluded: boolean;
  achievedStarRating: number | null;
};

export type PDFFutureProgress = {
  Actions: {}[];
  CriteriaId: number;
  CriteriaName: string;
  Id: number;
  IsExceeded: boolean;
  Note: string;
};

export type PDFStandard = {
  Actions: {
    CreatedDate: string;
    Description: string;
    Id: number;
    IsAlreadyComplete: boolean;
    Name: string;
    StandardId: number;
  }[];
  Description: string;
  Evaluations: { Comment: string; Id: number; StandardId: number; Status: number }[];
  Id: number;
  JLComment: string | null;
  Name: string;
  StarRating: number;
  Status: number;
  Uid: string;
};

export type PDFCriteria = {
  CriteriaGroupId: number;
  CriteriaGroupName: string;
  FutureProgress: PDFFutureProgress;
  Id: number;
  Name: string;
  Overview: {};
  Standards: PDFStandard[];
  Status: number;
  Uid: string;
};

export type PDFType = {
  AchievedStarRating: StarRating;
  ActionPlanTimeline: number;
  ClubName: string;
  ClubOverview: string;
  Criteria: PDFCriteria[];
  DevelopmentAreas: string;
  EvaluationTeam: { DisplayName: string; Email: string; Id: number; ImageBase64: string; Role: string }[];
  ExecutiveSummary: { Year: number; TargetStarRating: StarRating; AchievedStarRating: StarRating };
  GlossaryOfTerms: [{}];
  Id: number | null;
  ImageBase64: string;
  IsActionPlanIncluded: boolean;
  JapaneseClubName: string | null;
  KeyAchievements: string;
  Overview: {};
  ReportDate: string;
  ReportType: ReportType;
  Sessions: {
    Date: string;
    Id: number;
    Name: string;
    Observed: {}[];
    Required: string[];
    TotalMinutes: number;
    Type: number;
    Conducted: {
      DisplayName: string;
      Email: string | null;
      Id: number;
      ImageBase64: string | null;
      Role: number | null;
    }[];
  }[];
};


export const DayLabels:any = {
  mon: 'DC_7_MON',
  tue: 'DC_7_TUE',
  wed: 'DC_7_WED',
  thur: 'DC_7_THU',
  fri: 'DC_7_FRI',
  sat: 'DC_7_SAT',
  sun: 'DC_7_SUN',
};

export const labelPicker = (label: string) => {
  switch (label) {
    case 'Income':
      return 'DC_3_INCOME';
    case 'Expense':
      return 'DC_3_EXPENSE';

    case 'Membership fee income':
      return 'DC_3_MEMBERSHIP_FEE_INCOME';
    case 'J League Support Fund':
      return 'DC_3_J_LEAGUE_SUPPORT_FUND';

    case 'Travel':
      return 'DC_3_TRAVEL';
    case 'Facilities':
      return 'DC_3_FACILITIES';
    case 'Equipment/Fixtures':
      return 'DC_3_EQUIPMENT_FIXTURES';
    case 'Labour Cost':
      return 'DC_3_LABOUR_COST';
    case 'Other':
      return 'DC_3_OTHER';

    case 'U12/Development Centre':
      return 'DC_3_DEV_CENTRE';
    case 'U15s':
      return 'DC_3_U15S';
    case 'U18s':
      return 'DC_3_U18S';
    case 'Soccer School':
      return 'DC_3_SOCCER_SCHOOL';
    case 'Club Total':
      return 'DC_3_CLUB_TOTAL';
    case 'Academy Expense':
      return 'ACADEMY_EXPENSE';
    case 'Academy Income':
      return 'ACADEMY_INCOME';
    default:
      return label;
  }
};

export const clubsData = [
{id:3139, ename: 'KASHIMA ANTLERS', jname: '鹿島アントラーズ'},
{id:3140, ename: 'FC RYUKYU', jname: 'FC琉球'},
{id:3141, ename: 'KYOTO SANGA F.C.', jname: '京都サンガ'},
{id:3142, ename: 'THESPA GUNMA', jname: '群馬'},
{id:3143, ename: 'TOCHIGI SC', jname: '栃木SC'},
{id:3144, ename: 'V.VAREN NAGASAKI', jname: 'V・ファーレン長崎'},
{id:3145, ename: 'VENTFORET KOFU', jname: 'ヴァンフォーレ甲府'},
{id:3146, ename: 'TOKYO VERDY', jname: '東京ヴェルディ'},
{id:3147, ename: 'MATSUMOTO YAMAGA F.C.', jname: '松本山雅FC'},
{id:3148, ename: 'FC MACHIDA ZELVIA', jname: 'FC町田ゼルビア'},
{id:3149, ename: 'ZWEIGEN KANAZAWA', jname: 'ツエーゲン金沢'},
{id:3151, ename: 'FUKUSHIMA UNITED FOOTBALL CLUB', jname: '福島ユナイテッド'},
{id:3152, ename: 'GAINARE TOTTORI', jname: 'ガイナーレ鳥取'},
{id:3153, ename: 'FC GIFU', jname: 'FC岐阜'},
{id:3154, ename: 'FC.IMABARI', jname: 'FC今治'},
{id:3155, ename: 'IWATE GRULLA MORIOKA', jname: 'グルージャ盛岡'},
{id:3156, ename: 'KAGOSHIMA UNITED FC', jname: '鹿児島ユナイテッド'},
{id:3157, ename: 'KAMATAMARE SANUKI', jname: 'カマタマーレ讃岐'},
{id:3158, ename: 'KATALLER TOYAMA', jname: 'カターレ富山'},
{id:3159, ename: 'FUJIEDA MYFC', jname: '藤枝MYFC'},
{id:3160, ename: 'AC NAGANO PARCEIRO', jname: 'AC長野パルセイロ'},
{id:3161, ename: 'ROASSO KUMAMOTO', jname: 'ロアッソ熊本'},
{id:3162, ename: 'TEGEVAJARO MIYAZAKI', jname: 'テゲバジャーロ宮崎'},
{id:3163, ename: 'RENOFA YAMAGUCHI FC', jname: 'レノファ山口'},
{id:3164, ename: 'MONTEDIO YAMAGATA', jname: 'モンテディオ山形'},
{id:3165, ename: 'JUBILO IWATA', jname: 'ジュビロ磐田'},
{id:3166, ename: 'JEF UNITED CHIBA', jname: 'ジェフユナイテッド市原・千葉'},
{id:3168, ename: 'SHONAN BELLMARE', jname: '湘南ベルマーレ'},
{id:3171, ename: 'YOKOHAMA F･MARINOS', jname: '横浜Fマリノス'},
{id:3172, ename: 'KAWASAKI FRONTALE', jname: '川崎フロンターレ'},
{id:3173, ename: 'GAMBA OSAKA', jname: 'ガンバ大阪'},
{id:3174, ename: 'NAGOYA GRAMPUS', jname: '名古屋グランパスエイト'},
{id:3175, ename: 'URAWA REDS', jname: '浦和レッズ'},
{id:3176, ename: 'KASHIWA REYSOL', jname: '柏レイソル'},
{id:3177, ename: 'SHIMIZU S-PULSE', jname: '清水エスパルス'},
{id:3178, ename: 'VANRAURE HACHINOHE', jname: 'ヴァンラーレ八戸FC'},
{id:3179, ename: 'SAGAN TOSU', jname: 'サガン鳥栖'},
{id:3180, ename: 'OITA TRINITA', jname: '大分トリニータ'},
{id:3181, ename: 'VEGALTA SENDAI', jname: 'ベガルタ仙台'},
{id:3182, ename: 'VISSEL KOBE', jname: 'ヴィッセル神戸'},
{id:3183, ename: 'TOKUSHIMA VORTIS', jname: '徳島ヴォルティス'},
{id:3185, ename: 'OMIYA ARDIJA', jname: '大宮アルディージャ'},
{id:3189, ename: 'GIRAVANZ KITAKYUSHU', jname: 'ギラヴァンツ北九州'},
{id:3190, ename: 'MITO HOLLYHOCK', jname: '水戸ホーリーホック'},
{id:3191, ename: 'SANFRECCE HIROSHIMA', jname: 'サンフレッチェ広島'},
{id:3192, ename: 'Y.S.C.C. YOKOHAMA', jname: '水戸ホーリーホック'},
{id:3207, ename: 'AZUL CLARO NUMAZU', jname: 'アスルクラロ沼津'},
{id:3224, ename: 'AVISPA FUKUOKA', jname: 'アビスパ福岡'},
{id:3226, ename: 'CEREZO OSAKA', jname: 'セレッソ大阪'},
{id:3243, ename: 'ALBIREX NIIGATA', jname: 'アルビレックス新潟'},
{id:3245, ename: 'BLAUBLITZ AKITA', jname: 'ブラウブリッツ秋田'},
{id:3246, ename: 'EHIME FC', jname: '愛媛FC'},
{id:3247, ename: 'FAGIANO OKAYAMA', jname: 'ファジアーノ岡山'},
{id:3251, ename: 'HOKKAIDO CONSADOLE SAPPORO', jname: '北海道コンサドーレ札幌'},
{id:3253, ename: 'S.C.SAGAMIHARA', jname: 'SC相模原'},
{id:3254, ename: 'F.C.TOKYO', jname: 'FC東京'},
{id:3255, ename: 'YOKOHAMA FC', jname: '横浜FC'},
{id:3275, ename: 'IWAKI FC', jname: 'いわきFC'},
{id:3279, ename: 'F.C. OSAKA', jname: 'FC大阪'},
{id:3280, ename: 'NARA CLUB', jname: '奈良クラブ'},
]